/* ==========================================================================
   IRON FIT — Loading gate controller

   Automatic: no click, no button. Holds for ~1s while the logo lands and the
   bar fills, then lifts on its own. Tapping or pressing a key skips ahead.

   Load in <head> WITHOUT defer so html.ifz-gate lands before the first paint
   — otherwise the navbar and the showroom flash before the gate covers them.
   The markup is built here, so the page's HTML needs nothing but this tag.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__ifzIntro) return;
  window.__ifzIntro = 1;

  var root = document.documentElement;

  /* Set immediately: holds the page back before anything can paint. */
  root.classList.add('ifz-gate');

  /* --- tuning ---------------------------------------------------------- */
  var HOLD = 1000;   /* the promised second */
  var CAP  = 2200;   /* never hold longer than this waiting on window.load */

  /* MUST be the same photograph the showroom opens on (.show__pic), or the
     gate resolves one picture sharp and then dissolves into a different one
     — which reads as a glitch, not a transition. Sharing the file also means
     it is decoded and warm by the time the gate lifts, so nothing pops in. */
  var HERO = 'assets/ifz-home-scene.jpg';

  /* The theme hands us the real CDN URLs in window.IFZ_HERO, because Liquid
     already knows them and this script runs before ifz-cart.js has defined
     IFZA. On the static build neither exists and the plain path is right. */
  function heroUrl(kind) {
    var H = window.IFZ_HERO;
    if (H && H.jpg) return kind === 'webp' ? H.webp : H.jpg;
    var p = window.IFZA ? window.IFZA(HERO) : HERO;
    return kind === 'webp' ? p.replace(/\.jpg$/, '.webp') : p;
  }

  /* Flip to true to show the gate once per browser session instead of on
     every load. Returning visitors then land straight on the showroom. */
  var ONCE_PER_SESSION = false;
  var KEY = 'ifz-intro-seen';

  function seen() {
    try { return sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  }
  function markSeen() {
    try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* private mode */ }
  }

  function now() {
    return (window.performance && performance.now) ? performance.now() : +new Date();
  }

  /* Fired once, the moment the page is uncovered. Anything that wants to make
     an entrance listens for this instead of guessing at a delay. Late
     listeners are covered by the flag. */
  function reveal() {
    window.__ifzRevealed = 1;
    try { window.dispatchEvent(new CustomEvent('ifz:reveal')); } catch (e) {
      var ev = document.createEvent('Event');
      ev.initEvent('ifz:reveal', false, false);
      window.dispatchEvent(ev);
    }
  }

  /* ---------------------------------------------------------------------
     Markup. Built here rather than in the page so the gate survives any
     rewrite of the HTML — an existing #ifzIntro is reused and refilled.
     --------------------------------------------------------------------- */
  function build() {
    var gate = document.getElementById('ifzIntro');
    if (!gate) {
      gate = document.createElement('div');
      gate.id = 'ifzIntro';
    }

    gate.className = 'ifzi';
    gate.setAttribute('role', 'status');
    gate.setAttribute('aria-live', 'polite');
    gate.setAttribute('aria-label', 'Loading your gym');

    gate.innerHTML =
      '<div class="ifzi__bg" aria-hidden="true">' +
        /* WebP with the JPEG underneath — the showroom below shows the very
           same file, so both must offer the same pair or it is fetched twice */
        /* Through IFZA: on Shopify this file is on the CDN and the home page
           is "/", so a bare 'assets/…' asks for /assets/… and 404s. */
        '<picture>' +
          '<source type="image/webp" srcset="' + heroUrl('webp') + '">' +
          '<img src="' + heroUrl('jpg') + '" alt="" fetchpriority="high" decoding="async">' +
        '</picture>' +
      '</div>' +
      '<div class="ifzi__scrim" aria-hidden="true"></div>' +
      '<div class="ifzi__core">' +
        '<span class="ifzi__mark" aria-hidden="true">' +
          '<svg viewBox="0 0 32 32" fill="none" stroke="#35e02a" stroke-width="2.6" stroke-linecap="round">' +
            '<path d="M6 11v10M11 8v16M21 8v16M26 11v10"/>' +
            '<path d="M11 16h10" stroke-width="2.2"/>' +
          '</svg>' +
        '</span>' +
        '<span class="ifzi__word">Iron<b>Fit</b></span>' +
        '<p class="ifzi__msg">Loading your gym' +
          '<span class="ifzi__dots" aria-hidden="true"><i></i><i></i><i></i></span>' +
        '</p>' +
        '<div class="ifzi__bar" aria-hidden="true"><span></span></div>' +
      '</div>';

    /* The bar finishes exactly when the gate lifts, however long that is. */
    gate.style.setProperty('--ifzl-dur', HOLD + 'ms');

    if (gate.parentNode !== document.body) {
      document.body.insertBefore(gate, document.body.firstChild);
    }
    return gate;
  }

  function init() {
    /* Already seen this session — release without ever showing the gate. */
    if (ONCE_PER_SESSION && seen()) {
      var old = document.getElementById('ifzIntro');
      if (old) old.remove();
      root.classList.remove('ifz-gate');
      reveal();
      return;
    }

    var gate = build();
    var start = now();
    var done = false;

    function release() {
      if (done) return;
      done = true;
      markSeen();

      /* 0ms — the photo resolves sharp, the logo flies off. */
      gate.classList.add('is-out');

      /* 380ms — the gate dissolves while the site rises through it. The event
         is what the machine deck waits on to start its own entrance, so the
         two are choreographed rather than racing each other. */
      setTimeout(function () {
        gate.classList.add('is-gone');
        root.classList.remove('ifz-gate');
        root.classList.add('ifz-lit');
        reveal();
      }, 380);

      /* 1580ms — entrance over. .ifz-lit comes off so no transform is left
         on <main> to trap position:fixed children (the cart bar, overlays). */
      setTimeout(function () { root.classList.remove('ifz-lit'); }, 1580);

      /* 1700ms — gate out of the DOM entirely. */
      setTimeout(function () { if (gate.parentNode) gate.remove(); }, 1700);
    }

    /* Let people skip. The gate covers the viewport, so any tap lands here. */
    gate.addEventListener('click', release);
    gate.addEventListener('touchstart', release, { passive: true });
    document.addEventListener('keydown', function (e) {
      if (!done && (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ')) release();
    });

    /* Hold for HOLD ms. If the page is still loading past that, wait for it
       — but never past CAP, because a stalled image must not strand anyone. */
    function wait(ms) { return Math.max(0, ms); }

    if (document.readyState === 'complete') {
      setTimeout(release, wait(HOLD - (now() - start)));
      return;
    }

    var hard = setTimeout(release, wait(Math.max(HOLD, CAP) - (now() - start)));

    window.addEventListener('load', function () {
      clearTimeout(hard);
      setTimeout(release, wait(HOLD - (now() - start)));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
